/**
 * CategoriasCajaPage - Catálogo de categorías de gastos e ingresos de caja
 */
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Add as AddIcon, FilterList } from '@mui/icons-material';
import {
  PageHeader,
  LoadingSpinner,
  DataTable,
} from '../components/common';
import { useNotification } from '../context/NotificationContext';
import * as cajaService from '../services/cajaService';

const categoriaSchema = yup.object().shape({
  nombre: yup.string().required('El nombre es requerido'),
  tipo: yup.string().oneOf(['ingreso', 'egreso']).required('El tipo es requerido'),
  descripcion: yup.string(),
  activa: yup.boolean(),
});

const CategoriasCajaPage = () => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [filterTipo, setFilterTipo] = useState('all');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(categoriaSchema),
    defaultValues: {
      nombre: '',
      tipo: 'egreso',
      descripcion: '',
      activa: true,
    },
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterTipo !== 'all') {
        filters.tipo = filterTipo;
      }
      const data = await cajaService.getCategorias(filters);
      setCategorias(data || []);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      showError('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filterTipo]);

  const handleOpenCreate = () => {
    setEditingCategoria(null);
    reset({ nombre: '', tipo: 'egreso', descripcion: '', activa: true });
    setOpenDialog(true);
  };

  const handleOpenEdit = (categoria) => {
    setEditingCategoria(categoria);
    reset({
      nombre: categoria.nombre,
      tipo: categoria.tipo,
      descripcion: categoria.descripcion || '',
      activa: categoria.activa !== false,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategoria(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      if (editingCategoria) {
        await cajaService.updateCategoria(editingCategoria.id, data);
        showSuccess('Categoría actualizada correctamente');
      } else {
        await cajaService.createCategoria(data);
        showSuccess('Categoría creada correctamente');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error al guardar categoría:', error);
      showError(error.response?.data?.error || 'Error al guardar la categoría');
    }
  };

  const columns = [
    {
      field: 'nombre',
      headerName: 'Nombre',
    },
    {
      field: 'tipo',
      headerName: 'Tipo',
      renderCell: (row) => (
        <Chip
          label={row.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
          color={row.tipo === 'ingreso' ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'descripcion',
      headerName: 'Descripción',
    },
    {
      field: 'activa',
      headerName: 'Estado',
      type: 'status',
    },
  ];

  if (loading) {
    return <LoadingSpinner message="Cargando categorías..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Categorías de Caja"
        breadcrumbs={[{ label: 'Cajas' }, { label: 'Categorías' }]}
        actions={[
          {
            label: 'Nueva Categoría',
            icon: AddIcon,
            onClick: handleOpenCreate,
            variant: 'contained',
          },
        ]}
      />

      {/* Filtros */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FilterList />
        <ToggleButtonGroup
          value={filterTipo}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) {
              setFilterTipo(newValue);
            }
          }}
          size="small"
        >
          <ToggleButton value="all">Todas</ToggleButton>
          <ToggleButton value="ingreso">Ingresos</ToggleButton>
          <ToggleButton value="egreso">Egresos</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <DataTable
        columns={columns}
        data={categorias}
        onEdit={handleOpenEdit}
        searchPlaceholder="Buscar por nombre..."
        emptyMessage="No hay categorías registradas"
      />

      {/* Diálogo crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingCategoria ? 'Editar Categoría' : 'Nueva Categoría'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                {...register('nombre')}
                label="Nombre"
                fullWidth
                autoFocus
                error={!!errors.nombre}
                helperText={errors.nombre?.message}
              />

              <Controller
                name="tipo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Tipo de movimiento"
                    fullWidth
                    error={!!errors.tipo}
                    helperText={errors.tipo?.message}
                  >
                    <MenuItem value="egreso">Egreso / Gasto</MenuItem>
                    <MenuItem value="ingreso">Ingreso</MenuItem>
                  </TextField>
                )}
              />

              <TextField
                {...register('descripcion')}
                label="Descripción"
                fullWidth
                multiline
                rows={2}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    {...register('activa')}
                    defaultChecked={editingCategoria?.activa !== false}
                  />
                }
                label="Activa"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingCategoria ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CategoriasCajaPage;
